/** @prettier */
import classNames from 'classnames';
import { List, Map } from 'immutable';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import Carousel from '@xanadu/ui-shared/components/Carousel';
import GlobalBannerPortal from '@xanadu/ui-shared/components/GlobalBannerPortal';
import EventsGroup from '@xanadu/ui-shared/components/GlobalEventsPage/EventsGroup';
import useVisibleEventIds from '@xanadu/ui-shared/components/GlobalEventsPage/RacingSportPage/hooks/useVisibleEventIds';
import SubCategory from '@xanadu/ui-shared/components/GlobalEventsPage/SubCategory';
import { ALL_MARKET_TYPES } from '@xanadu/ui-shared/components/GlobalEventsPage/settings';
import HaveYourSayWidget from '@xanadu/ui-shared/components/HaveYourSayWidget';
import MBZeroInfoPanel from '@xanadu/ui-shared/components/MBZeroInfoPanel';
import MBZeroSportTiles from '@xanadu/ui-shared/components/MBZeroSportTiles';
import PopularMarkets from '@xanadu/ui-shared/components/PopularMarkets';
import PopularSports from '@xanadu/ui-shared/components/PopularSports';
import PreCannedBetBuilders from '@xanadu/ui-shared/components/PreCannedBetBuilders';
import RaceCards from '@xanadu/ui-shared/components/RaceCards';
import { EMPTY_LIST, MOBILE_BREAKPOINT, SPORT_URL_NAMES } from '@xanadu/ui-shared/constants';
import ChildrenPropsContext from '@xanadu/ui-shared/contexts/ChildrenPropsContext';
import { useReduxBridge } from '@xanadu/ui-shared/contexts/ReduxBridgeContext';
import { usePolling } from '@xanadu/ui-shared/hooks';
import {
  isExchangeProduct,
  isFuturesOrPredictionsProduct,
  isMBZeroProduct,
} from '@xanadu/ui-shared/modules/application/selectors';
import { requestEventsPageData as requestEventsPageDataAct } from '@xanadu/ui-shared/modules/event/actions';

import { getClientOrigin } from '../../utils';

import styles from './Homepage.module.scss';

const MBZERO_PAGE_TITLE = 'Matchbook Zero | Zero Percent Commission & Zero Margin Betting';
const MBZERO_PAGE_DESCRIPTION =
  'Try Matchbook Zero today. Zero Percent Commission. Zero Margin. Get the best price on your bets with Matchbook Zero.';

const POPULAR_CATEGORIES = 5;
const POLLING_INTERVAL = 30000;

const Homepage = ({
  history,
  applicationVisible,
  clientName,
  defaultPageDescription = '',
  events: allEvents,
  navEventIds,
  pageDescription = '',
  pageTitle = '',
  precannedBetslipsData,
  raceCardWidgetEnabled,
  requestEventsPageData,
  tags,
  widthSize,
  isExchange,
  isFuturesOrPredictions,
  isMBZero,
}) => {
  const { iosAppId } = useReduxBridge() || {};

  const {
    excludedSports = [],
    featuresFlags: { raceCardsEnabled = true, enabledTrendingMarkets = false } = {},
  } = React.useContext(ChildrenPropsContext) || {};

  const origin = getClientOrigin(clientName);

  const overridePageTitle = !isMBZero ? pageTitle : MBZERO_PAGE_TITLE;

  const overridePageDescription = !isMBZero ? pageDescription : MBZERO_PAGE_DESCRIPTION;

  const isMobile = widthSize <= MOBILE_BREAKPOINT;

  const popularCompetitions = useMemo(() => {
    if (isExchange) {
      return [];
    }
    const currentCategory = tags.find((tag) => tag.url === '/soccer') || {};

    const { children = [] } = currentCategory;
    const subCategories = new List(
      children.map((metaTagId) => tags.find((t) => t.uuid === metaTagId) || {})
    );
    const groupedTags = subCategories.groupBy(({ type }) => type);
    const competitions = groupedTags.get('COMPETITION') || EMPTY_LIST;
    return competitions.size ? competitions : subCategories;
  }, [isExchange, tags]);

  const eventsChildren = ['horse-racing'];
  const eventsChildrenHash = eventsChildren.join('_');
  const marketTypes = ['outright', 'outright_ded_fact'].join(',');

  const { visibleEventIds, loading } = useVisibleEventIds(navEventIds, eventsChildrenHash);

  const precannedData = useMemo(
    () =>
      precannedBetslipsData.filter((precanned) => {
        const eventId = precanned.event.id;
        const { inRunningFlag, status } = allEvents.get(eventId) || {};
        return !inRunningFlag && !['closed', 'unavailable'].includes(status);
      }),
    [precannedBetslipsData, allEvents]
  );

  const tagUrlNamesList = new List(
    popularCompetitions
      .slice(0, POPULAR_CATEGORIES)
      .map(({ urlName: subCategoryUrlName }) => `soccer|${subCategoryUrlName}`)
  );

  const tagUrlNamesListHash = tagUrlNamesList.join('_');

  const displayQuickSports = useMemo(() => {
    const grouped = tags.groupBy(({ urlName }) => urlName);
    const soccerTag = grouped.get(SPORT_URL_NAMES.soccer);
    const horseRacingTag = grouped.get(SPORT_URL_NAMES.horseRacing);

    return isMBZero && isMobile && !!soccerTag && !!horseRacingTag;
  }, [isMBZero, isMobile, tags]);

  const handleNavigate = ({ url }) => {
    history.push(url);
  };

  usePolling(
    () => {
      if (isMBZero && tagUrlNamesListHash) {
        requestEventsPageData({
          tagUrlNamesList,
          marketTypes: ALL_MARKET_TYPES.join(','),
          id: 'soccer',
          onlyEventsWithin48Hours: true,
          offset: 0,
          perPage: 25,
        });
      }
    },
    applicationVisible,
    POLLING_INTERVAL,
    [isMBZero, tagUrlNamesListHash]
  );

  usePolling(
    () => {
      if (isMBZero) {
        requestEventsPageData({
          tagUrlNamesList: new List(eventsChildren),
          marketTypes,
          id: eventsChildren[0],
          perPage: 3,
          offset: 0,
          perTagUrlNames: 3,
        });
      }
    },
    applicationVisible,
    POLLING_INTERVAL,
    [isMBZero]
  );

  useEffect(() => {
    if (isMBZero && tagUrlNamesListHash) {
      requestEventsPageData({
        tagUrlNamesList,
        marketTypes: ALL_MARKET_TYPES.join(','),
        id: 'soccer',
        onlyEventsWithin48Hours: true,
        offset: 0,
        perPage: 25,
      });
    }
  }, [isMBZero, tagUrlNamesListHash]);

  useEffect(() => {
    if (isMBZero) {
      // For horse-racing events
      requestEventsPageData({
        tagUrlNamesList: new List(eventsChildren),
        marketTypes,
        id: eventsChildren[0],
        perPage: 3,
        offset: 0,
        perTagUrlNames: 3,
      });
    }
  }, [isMBZero]);

  const contentClassNames = classNames({
    [styles.main]: true,
    [styles.quickLinksActive]: isMBZero && isMobile && displayQuickSports,
  });

  return (
    <div className={contentClassNames}>
      <Helmet>
        <title>{overridePageTitle}</title>
        <meta name="description" content={overridePageDescription || defaultPageDescription} />
        <meta
          name="apple-itunes-app"
          content={`app-id=${iosAppId}`}
          app-argument={`https://${origin}`}
        />
      </Helmet>
      <GlobalBannerPortal />
      <Carousel pageType="Homepage" />
      {displayQuickSports && (
        <div className={styles.quickSports}>
          <MBZeroSportTiles navigateFunction={handleNavigate} size="small" />
        </div>
      )}
      {isMBZero && isMobile && <MBZeroInfoPanel />}
      {isExchange && isMobile && <PopularSports />}

      {raceCardsEnabled && !excludedSports.includes('horse-racing') && (
        <RaceCards raceCardWidgetEnabled={raceCardWidgetEnabled} size="large" />
      )}
      {isExchange && <PreCannedBetBuilders data={precannedData} />}
      <div className={styles.events}>
        {isFuturesOrPredictions && enabledTrendingMarkets && <HaveYourSayWidget />}
        {(isExchange || isFuturesOrPredictions) && <PopularMarkets />}
        {isMBZero && (
          <Fragment>
            {popularCompetitions
              .slice(0, POPULAR_CATEGORIES)
              .map(({ uuid, url = '', name: subCategoryName }) => (
                <SubCategory
                  key={uuid}
                  name={subCategoryName}
                  segments={url.split('/').filter((s) => s)}
                  layout="one-x-two"
                  onlyEventsWithin48Hours
                  isMBZero
                />
              ))}
            <EventsGroup
              tags={eventsChildren}
              layout="outright"
              eventIds={visibleEventIds}
              loading={loading}
              isOutright
              isHomepage
              noHeader
            />
          </Fragment>
        )}
      </div>
    </div>
  );
};

Homepage.propTypes = {
  clientName: PropTypes.string.isRequired,
  defaultPageDescription: PropTypes.string,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  pageTitle: PropTypes.string,
  pageDescription: PropTypes.string,
  widthSize: PropTypes.number.isRequired,
  raceCardWidgetEnabled: PropTypes.bool,
  applicationVisible: PropTypes.bool.isRequired,
  tags: PropTypes.instanceOf(List).isRequired,
  navEventIds: PropTypes.instanceOf(Map).isRequired,
  requestEventsPageData: PropTypes.func.isRequired,
  precannedBetslipsData: PropTypes.instanceOf(List).isRequired,
  events: PropTypes.instanceOf(Map),
  isExchange: PropTypes.bool,
  isFuturesOrPredictions: PropTypes.bool,
  isMBZero: PropTypes.bool,
};

const mapDispatchToProps = {
  requestEventsPageData: requestEventsPageDataAct,
};

function mapStateToProps(state) {
  return {
    applicationVisible: state.getIn(['application', 'visible']),
    product: state.getIn(['application', 'product']),
    widthSize: state.getIn(['application', 'widthSize']),
    raceCardWidgetEnabled: state.getIn(['application', 'raceCardWidgetEnabled']),
    tags: state.getIn(['navigation', 'tags'], EMPTY_LIST),
    navEventIds: state.getIn(['navigation', 'eventIds']),
    events: state.get('event'),
    isFuturesOrPredictions: isFuturesOrPredictionsProduct(state),
    isMBZero: isMBZeroProduct(state),
    isExchange: isExchangeProduct(state),
    precannedBetslipsData: state.getIn(['events', 'precannedBetslips', 'popular'], EMPTY_LIST),
    clientName: state.getIn(['application', 'clientName']),
    pageTitle: state.getIn(['application', 'homepageTitle']),
    pageDescription: state.getIn(['application', 'homepageDescription']),
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Homepage));
